import React, { Component } from 'react';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CustomTable from '../../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import moment from 'moment/moment';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import { AlertLog } from '../../../domain/watchlist/WatchlistAlertDomains';
import NavigationUtil from '../../../util/NavigationUtil';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { WatchlistAlertDomains } from '../../../domain/watchlist/WatchlistAlertDomains';
import { connect } from 'react-redux';
import { RootState } from '../../../redux/store';
import { ConnectedProps } from 'react-redux/es/exports';
import { query as q } from '@trlogic/client-js';
import QueryBuilder = q.QueryBuilder;
import eq = q.eq;
import Icon from '../../../icons/Icon';
import LanguageUtil from '../../../util/LanguageUtil';
import CollectionNames from '../../../constant/CollectionNames';

class WatchlistAlertAuditComponent extends Component<Props, State> {
  public state: State = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private commentSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  public render() {
    const { logs, rows, currentPage, totalRecord, loading } = this.state;
    return (
      <div className={'h-full w-full p-5 max-h-[calc(100vh-20rem)] overflow-y-scroll'}>
        <div className={'flex min-h-[24px] justify-end bg-white items-center pb-2 w-full'}>
          <a className={`flex hover:fill-primary items-center justify-center relative group`} onClick={this.onExport} data-testid='alert_audit_export_toggle_button'>
            <Icon icon={'export'} size={20} color={'#4F4F4F'} className={'cursor-pointer'} />
            <div
              className={'z-50 -bottom-5 rounded absolute hidden px-[10px] py-[2px] text-2xs text-white bg-primary group-hover:flex'}
              data-testid='alert_audit_export_toggle_button_text'
            >
              {LanguageUtil.get({ id: 'common.label.export', defaultMessage: 'Export' })}
            </div>
          </a>
        </div>
        <CustomTable columns={this.getColumns()} value={logs} />
        <CustomPaginator
          currentPage={currentPage}
          key='watchlist-alert-audit'
          rows={rows}
          totalRecords={totalRecord}
          loading={loading}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          onPageChange={this.onPageChange}
        />
      </div>
    );
  }

  public componentDidMount(): void {
    this.startTracker();
    this.loadUsers();
  }

  public async componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const endpoint = this.getEndpoint();
    const _query = this.getQuery();
    this.commentSubscription = subscribe.Formica.subscribe(endpoint, _query, undefined, 'elasticsearch');
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.commentSubscription]);
  };

  private tracker = async () => {
    if (this.commentSubscription?.ready()) {
      await this.buildLog();
      await this.fetchCount();
    }
  };

  private stopTracker = () => {
    this.commentSubscription?.stop();
    this.computation?.stop();
  };

  private loadUsers = async () => {
    try {
      const usersQuery = QueryBuilder.select().select('username', 'firstName', 'lastName').from(CollectionNames.USERS).build();
      const users: { username: string; firstName?: string; lastName?: string }[] = await this.repository.findMany(usersQuery);
      const usernameToFullName = users.reduce<Record<string, string>>((acc, u) => {
        const firstname = u.firstName ?? '';
        const lastname = u.lastName ?? '';
        const fullName = `${firstname} ${lastname}`.trim();
        acc[u.username] = fullName.length > 0 ? fullName : u.username;
        return acc;
      }, {});
      this.setState({ usernameToFullName });
    } catch (e) {}
  };

  private getEndpoint = () => {
    return `kyc_alert_log_${NavigationUtil.getTenant()}_*`;
  };

  private getQuery = () => {
    const { alertId } = this.props;
    const { rows, currentPage } = this.state;
    const endpoint = this.getEndpoint();
    return QueryBuilder.select()
      .from(endpoint)
      .limit(rows)
      .skip((currentPage - 1) * rows)
      .where(eq('alertId', alertId))
      .orderBy({ field: 'timestamp', operator: 'DESC' })
      .build();
  };

  private buildLog = async () => {
    const logs = await this.repository.findMany<AlertLog & { content?: string; assignee?: string; reason?: string }>(this.getQuery());
    this.setState({ logs, loading: false });
  };

  private fetchCount = async () => {
    try {
      const _query = this.getQuery();
      const countQuery = {
        ..._query,
        order: undefined,
        group: undefined,
        fields: [],
        top: undefined,
        offset: undefined,
      } as q.SelectQuery;
      // İleride server tarafında export ve kesin count gerekirse eklenecek.
      const logs = await this.repository.findMany<AlertLog>({ ..._query, limit: 1000, skip: 0 } as any);
      this.setState({ totalRecord: logs.length });
    } catch (e) {}
  };

  private onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ currentPage: event.page, rows: event.rows, loading: true }, this.buildLog);
  };

  private getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'username',
        header: LanguageUtil.get({ id: 'common.label.username', defaultMessage: 'Username' }),
        className: 'w-1/4',
        body: (record: AlertLog) => {
          return (
            <div className={'flex items-start text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={this.state.usernameToFullName[record.username] ?? record.username} />
            </div>
          );
        },
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: 'common.label.type', defaultMessage: 'Type' }),
        className: 'w-1/4',
        body: (record: AlertLog) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record.type} />
            </div>
          );
        },
      },
      {
        field: 'detail',
        header: LanguageUtil.get({ id: 'common.label.detail', defaultMessage: 'Detail' }),
        className: 'w-1/4',
        body: (record: AlertLog & { content?: string; assignee?: string; reason?: string }) => {
          let detail: string;
          switch (record.type) {
            case 'ASSIGNEE':
              detail = this.state.usernameToFullName[record.assignee ?? ''] ?? record.assignee ?? '';
              break;
            case 'CLOSE':
              detail = record.reason ?? '';
              break;
            case 'COMMENT':
              detail = record.content ?? '';
              break;
            default:
              detail = record.content ?? 'In Review';
          }
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={detail} />
            </div>
          );
        },
      },
      {
        field: 'timestamp',
        header: LanguageUtil.get({ id: 'common.label.timestamp', defaultMessage: 'Timestamp' }),
        className: 'w-1/4',
        body: (record: AlertLog) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={moment(record.timestamp).format('LLLL').valueOf()} />
            </div>
          );
        },
      },
    ];
  };

  private onExport = () => {
    const { logs } = this.state;
    const { alertId } = this.props;
    if (!logs || logs.length === 0 || !alertId) {
      return;
    }
    const headers: string[] = ['Timestamp', 'Type', 'User', 'Detail', 'Alert ID'];
    const csvRows: string[] = [headers.join(',')];
    logs
      .slice()
      .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0))
      .forEach((log) => {
        const detail = (log as any).type === 'ASSIGNEE'
          ? (this.state.usernameToFullName[(log as any).assignee] ?? (log as any).assignee ?? '')
          : ( (log as any).type === 'CLOSE' ? ((log as any).reason ?? '') : (((log as any).content ? String((log as any).content) : '')) );
        const escapedDetail = detail ? `"${String(detail).replace(/"/g, '""')}"` : '';
        const row: string[] = [
          log.timestamp ? moment(log.timestamp).format('YYYY-MM-DD HH:mm:ss') : '',
          log.type || '',
          (this.state.usernameToFullName[log.username] ?? log.username) || '',
          escapedDetail,
          alertId,
        ];
        csvRows.push(row.join(','));
      });

    const csvContent: string = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url: string = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `watchlist_alert_audit_${alertId}_${moment().valueOf()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
}

interface Props extends ReduxProps, FormicaProps {
  alertId: string;
  kycLogData?: WatchlistAlertDomains;
}

class State {
  logs: (AlertLog & { content?: string; assignee?: string; reason?: string })[] = [];
  loading: boolean = true;
  rows = 10;
  totalRecord = 0;
  currentPage = 1;
  usernameToFullName: Record<string, string> = {};
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
});
const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(WatchlistAlertAuditComponent));


